import { createContext, useContext, useEffect } from "react";

export interface PageMeta {
  title: string;
  group:
    | "campaigns"
    | "finance"
    | "settings"
    | "content"
    | "analytics"
    | "data"
    | "workspace"
    | null;
}

/** Первая часть крошки шапки — подписи групп меню (design/nav.ts). */
export const GROUP_LABEL: Record<NonNullable<PageMeta["group"]>, string> = {
  campaigns: "Кампании",
  finance: "Финансы",
  settings: "Администрирование",
  content: "Контент",
  analytics: "Аналитика",
  data: "Клиенты и данные",
  workspace: "Рабочее место",
};

/** Крошка без группы (неизвестный путь). */
export const ROOT_LABEL = "CRM-маркетинг";

/**
 * Экран в крошке «группа › экран» назван как пункт меню (прототип «SMS-центр»):
 * пользователь видит в шапке то же слово, на которое нажал. Страницы без пункта
 * меню — редактор, каталог, результат, каналы — названы по смыслу.
 */
const STATIC: Record<string, PageMeta> = {
  "/home": { title: "Главная", group: "campaigns" },
  "/campaign-dashboard": { title: "Обзор и заказы", group: "campaigns" },
  "/campaigns": { title: "Кампании", group: "campaigns" },
  "/campaigns/new": { title: "Новая кампания", group: "campaigns" },
  "/campaigns/results": { title: "Статистика кампании", group: "campaigns" },
  "/recommendations": { title: "Возвращаемость", group: "campaigns" },
  "/finance": { title: "Обзор финансов", group: "finance" },
  "/billing": { title: "Баланс и счета", group: "finance" },
  "/spending": { title: "Расходы", group: "finance" },
  "/clients": { title: "Счета партнёров", group: "finance" },
  "/templates": { title: "Шаблоны сообщений", group: "content" },
  "/promo-codes": { title: "Промокоды", group: "content" },
  "/guests": { title: "Клиенты", group: "data" },
  "/data/actions": { title: "Действия клиентов", group: "data" },
  "/data/orders": { title: "Заказы", group: "data" },
  "/data/products": { title: "Продукты", group: "data" },
  "/segments": { title: "RFM-сегменты", group: "data" },
  "/reports/business": { title: "Метрики бизнеса", group: "analytics" },
  "/reports/goals": { title: "Цели бизнеса", group: "analytics" },
  "/reports/actions": { title: "Целевые действия", group: "analytics" },
  "/reports/lifecycle": { title: "Жизненный цикл", group: "analytics" },
  "/reports/cohorts": { title: "Когорты удержания", group: "analytics" },
  "/reports/segments": { title: "Отчёт по сегментам", group: "analytics" },
  "/reports/promotions": { title: "Отчёт по акциям", group: "analytics" },
  "/reports/consents": { title: "Согласия на рассылки", group: "analytics" },
  "/reports/uplift": { title: "Кому помогает рассылка", group: "analytics" },
  "/reports/scenarios": { title: "Отчёт по сценариям", group: "analytics" },
  "/reports/site": { title: "Конверсия сайта", group: "analytics" },
  "/analytics": { title: "Повторные покупки и LTV", group: "analytics" },
  "/settings": { title: "Настройки", group: "settings" },
  "/channels": { title: "Каналы отправки", group: "settings" },
  "/settings/integrations": { title: "Внешние системы", group: "settings" },
  "/settings/staff": { title: "Персонал", group: "settings" },
  "/settings/security": { title: "Группы безопасности", group: "settings" },
  "/settings/audit": { title: "Журнал действий", group: "settings" },
  "/audit": { title: "Журнал действий", group: "workspace" },
  "/preferences": { title: "Форматы и расчёты", group: "workspace" },
  "/problems": { title: "Проблемы и развитие", group: "workspace" },
  "/howto": { title: "Как это работает", group: "workspace" },
  "/notifications": { title: "Уведомления", group: "workspace" },
};

/** Заголовок шапки и группа крошки по пути. */
export function pageMetaFor(pathname: string): PageMeta {
  const path = pathname.replace(/\/+$/, "") || "/";
  const exact = STATIC[path];
  if (exact) return exact;
  if (/^\/campaigns\/[^/]+\/results$/.test(path))
    return { title: "Результат кампании", group: "campaigns" };
  if (/^\/campaigns\/[^/]+$/.test(path))
    return { title: "Кампания", group: "campaigns" };
  if (/^\/templates\/[^/]+$/.test(path))
    return { title: "Шаблон", group: "content" };
  return { title: "SMS-центр", group: null };
}

/** Переопределение заголовка шапки страницей (имя кампании и т.п.). Дефолт — без провайдера, no-op. */
export const PageTitleContext = createContext<{
  override: string | null;
  setOverride: (title: string | null) => void;
}>({ override: null, setOverride: () => {} });

/** Ставит заголовок шапки на время жизни страницы; null — вернуть заголовок по пути. */
export function usePageTitle(title: string | null): void {
  const { setOverride } = useContext(PageTitleContext);
  useEffect(() => {
    setOverride(title);
    return () => setOverride(null);
  }, [title, setOverride]);
}
